import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
  FileUploadTrigger,
} from '@components/ui/file-upload';
import { Button } from '@components/ui/button';
import { CloudUpload, X } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';

const FormFileUploadField = ({
  name,
  label,
  description,
  accept = 'image/*',
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024,
  multiple = false,
  required = false,
  rules = {},
}) => {
  const {
    control,
    setError,
    watch,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const error = errors[name];

  const originalUrl = watch(`${name}_original_url`);
  const currentFiles = watch(name);
  const showOriginal =
    originalUrl && (!currentFiles || currentFiles.length === 0);
  const isImage = (url) => {
    if (!url) return false;
    return (
      /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url) || accept.includes('image')
    );
  };
  const requiredRule = required
    ? {
        validate: (value, formValues) => {
          // Get the original URL from form values
          const currentOriginalUrl = formValues[`${name}_original_url`];
          console.log('Validating:', { currentOriginalUrl, value, name });
          
          // If there's an original file URL and no new files selected, it's valid
          if (currentOriginalUrl && (!value || value.length === 0)) {
            return true;
          }
          // If there's no original file, new files are required
          if (!currentOriginalUrl && (!value || value.length === 0)) {
            return t('{{field}} est requise', { field: label });
          }
          return true;
        },
      }
    : {};

  return (
    <Controller
      name={name}
      control={control}
      rules={{ ...requiredRule, ...rules }}
      render={({ field }) => (
        <div className="space-y-2">
          {label && (
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
          )}
          <FileUpload
            value={field.value || []}
            onValueChange={field.onChange}
            accept={accept}
            maxFiles={maxFiles}
            maxSize={maxSize}
            multiple={multiple}
            onFileReject={(_, message) => {
              setError(name, { message });
            }}
          >
            <FileUploadDropzone className="flex-row flex-wrap border-dotted text-center gap-1 items-center justify-center">
              <CloudUpload className="size-4" />
              {t('common|Drag and drop')}
              <FileUploadTrigger asChild>
                <Button variant="link" size="sm" className="p-0">
                  {t('common|choose files')}
                </Button>
              </FileUploadTrigger>
              {t('common|to upload')}
            </FileUploadDropzone>

            {showOriginal && (
              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                <div className="text-sm font-medium mb-2">
                  {t('common|Current file')}:
                </div>
                {isImage(originalUrl) ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={originalUrl}
                      alt="Current file"
                      className="w-16 h-16 object-cover rounded border"
                    />
                    <div className="text-xs text-muted-foreground">
                      {t('common|Upload a new file to replace')}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <a
                      href={originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {originalUrl.split('/').pop() || t('common|View file')}
                    </a>
                    <div className="text-xs text-muted-foreground">
                      ({t('common|Upload a new file to replace')})
                    </div>
                  </div>
                )}
              </div>
            )}

            <FileUploadList>
              {Array.isArray(field.value) &&
                field.value.map((file, index) => (
                  <FileUploadItem key={index} value={file}>
                    <FileUploadItemPreview />
                    <FileUploadItemMetadata />
                    <FileUploadItemDelete asChild>
                      <Button variant="ghost" size="icon" className="size-7">
                        <X />
                        <span className="sr-only">{t('common|Delete')}</span>
                      </Button>
                    </FileUploadItemDelete>
                  </FileUploadItem>
                ))}
            </FileUploadList>
          </FileUpload>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          {error && (
            <p className="text-sm font-medium text-destructive">
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  );
};
export default FormFileUploadField;
